import {
  Connection,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  Node,
  NodeProps,
  Position,
  ReactFlow,
  StepEdge,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  getOutgoers,
  getIncomers,
  Background,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ChangeEvent, FunctionComponent, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CloudNode } from './nodes/Cloud';
import { EdgeNode } from './nodes/Edge';
import { AssetNode } from './nodes/AssetNode';
import { SmartAssetNode } from './nodes/SmartAsset';
import { MachineNode } from './nodes/Machine';
import * as uuid from 'uuid';
import { cloneDeep, get, isEmpty, isEqual, lowerCase } from 'lodash';
import './styles/flow.scss';

import {
  CloudSchema,
  CloudUISchema,
  AssetSchema,
  AssetUISchema,
  EdgeSchema,
  EdgeUISchema,
  MachineSchema,
  MachineUISchema,
} from './panel-schemas';
import { ParamsPanel } from './panels/ParamsPanel';
import { EditorContext, TelemetryContext } from './context';
import Sidebar from './components/Sidebar';
import Modal from '../../../common/Modal';
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import SelectComponent from '../../../components/Select';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { buildUseCaseActions } from '../../../store/reducers/build-usecases';
import DataTable from '../../../components/DataTable';
import Chip from '@mui/material/Chip';
import { useSnackbar } from 'notistack';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import mqtt from 'mqtt';
import { sentenceCase } from 'change-case';
import ConnectionEdge from '../../../pages/FlowRenderer/react-flow/ConnectionEdges';
import { useParams } from 'react-router-dom';
import { Dispatch } from '@jsonforms/react';
import AssetConnectionEdge from './AssetConnectionEdge';
import { LoadingButton } from '@mui/lab';
import { connect, useDispatch } from 'react-redux';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { ReactComponent as AddCircleIcon } from '../../../assets/icons/main/plus-circle-icon.svg';
import dagre from '@dagrejs/dagre';
import { flushSync } from 'react-dom';
import PopUp from '../../../components/Popup';

import { ReactComponent as GatewayIcon } from '../../../assets/Gateway.svg';
import { ReactComponent as AlertIcon } from '../../../assets/icons/main/alertIcon.svg';
import { ReactComponent as CloseCircleIcon } from '../../../assets/icons/main/close-circle-icon.svg';
import { palette } from '../../../theme/palette';
import { factoryManagementActions } from '../../../store/reducers/factory-management';

const customRender = (payload: any, data: Record<string, any>): any => {
  if (Array.isArray(payload)) {
    return payload.map((item) => customRender(item, data)).filter((item) => item !== undefined && item !== null);
  }

  if (typeof payload === 'object' && payload !== null) {
    const result: any = {};
    for (const key of Object.keys(payload)) {
      const value = customRender(payload[key], data);
      if (value !== undefined && value !== null) {
        result[key] = value;
      }
    }
    return result;
  }

  if (typeof payload === 'string') {
    const exactMatch = payload.match(/^{{(.*?)}}$/);

    if (exactMatch) {
      const dataKey = exactMatch[1].trim();
      const value = dataKey.split('.').reduce((acc: any, part: string) => acc && acc[part], data);

      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
      return undefined;
    }

    return payload.replace(/{{(.*?)}}/g, (match, key) => {
      const value = key
        .trim()
        .split('.')
        .reduce((acc: any, part: string) => acc && acc[part], data);
      if (value === undefined || value === null) return '';
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
    });
  }

  return payload;
};

const normalizeScanResultRows = (rawResult: any, scanResultSchema: any): any[] => {
  if (rawResult === null || rawResult === undefined) return [];

  const schema = scanResultSchema && typeof scanResultSchema === 'object' ? scanResultSchema : {};
  const scanResultType = get(schema, 'type');
  const scanResultSchemaKey = get(schema, 'rootKey');
  const scanResultSchemaColumns = get(schema, 'columns', []);

  const flattenData = (tag: any): any[] => {
    if (!tag || typeof tag !== 'object') return [];
    const children = Array.isArray(tag.children) ? tag.children : [];
    return [tag, ...children.flatMap(flattenData)];
  };

  if (scanResultType === 'object') {
    const source =
      scanResultSchemaKey && rawResult && typeof rawResult === 'object'
        ? rawResult[scanResultSchemaKey]
        : rawResult;

    if (Array.isArray(source) && source.length > 0) {
      if (source[0] && typeof source[0] === 'object') {
        if (scanResultSchemaColumns.length) {
          return source.map((item: Record<string, any>) => {
            return scanResultSchemaColumns.reduce((acc: Record<string, any>, col: any) => {
              const key = get(col, 'value');
              if (!key) return acc;
              acc[key] = item?.[key];
              return acc;
            }, {} as Record<string, any>);
          });
        }
        return source;
      }

      if (typeof source[0] === 'string') {
        const firstColumn = get(scanResultSchemaColumns, '[0].value', 'Name');
        return source.map((value: string) => ({ [firstColumn]: value }));
      }

      return [];
    }

    if (source && typeof source === 'object') {
      const flattened = flattenData(source)
        .filter((tag: any) => !!tag?.type && tag?.datatype !== 'Unsupported')
        .map((tag: any) => {
          const next = { ...tag };
          delete next.children;
          return next;
        });

      if (flattened.length > 0) return flattened;

      if (scanResultSchemaColumns.length) {
        return [
          scanResultSchemaColumns.reduce((acc: Record<string, any>, col: any) => {
            const key = get(col, 'value');
            if (!key) return acc;
            acc[key] = get(source, key);
            return acc;
          }, {} as Record<string, any>),
        ];
      }

      return [source];
    }

    return [];
  }

  if (scanResultType === 'array') {
    return Array.isArray(rawResult) ? rawResult : [];
  }

  if (Array.isArray(rawResult)) {
    return rawResult;
  }

  return flattenData(rawResult)
    .filter((tag: any) => !!tag?.type && tag?.datatype !== 'Unsupported')
    .map((tag: any) => {
      const next = { ...tag };
      delete next.children;
      return next;
    });
};

const normalizeCommandId = (value: any) => String(value ?? '').trim();

const parseCommandResult = (value: any) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }
  return value;
};

const normalizeScanLifecycleText = (value: any) => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
};

const NON_TERMINAL_SCAN_RESULTS = new Set([
  'scan_command_received',
  'command_received',
  'accepted',
  'queued',
  'processing',
  'in_progress',
  'scan_started',
  'scanning_started',
  'started',
]);

const TERMINAL_STOP_SCAN_RESULTS = new Set([
  'stopped',
  'scan_stopped',
  'cancelled',
  'canceled',
  'aborted',
]);

const TERMINAL_ERROR_SCAN_RESULTS = new Set([
  'failed',
  'error',
  'scan_failed',
  'scan_error',
  'timeout',
  'timed_out',
]);

const EMPTY_ERROR_TEXTS = new Set(['', 'none', 'null', 'nil', 'no error', 'ok', 'success']);

const getLifecycleTextFromResult = (result: any) => {
  const candidates = [
    result,
    get(result, 'status'),
    get(result, 'state'),
    get(result, 'result'),
    get(result, 'message'),
  ];

  for (const candidate of candidates) {
    const normalized = normalizeScanLifecycleText(candidate);
    if (normalized) return normalized;
  }

  return '';
};

const normalizeErrorMessage = (raw: any) => {
  const text = String(raw ?? '').trim();
  if (!text) return '';
  return EMPTY_ERROR_TEXTS.has(text.toLowerCase()) ? '' : text;
};

const extractBulkScanEntries = (result: any): Array<[string, any]> => {
  const scanObj = get(result, 'scan');
  if (!scanObj || typeof scanObj !== 'object' || Array.isArray(scanObj)) return [];

  return Object.entries(scanObj).filter(([key, value]) => {
    if (!key) return false;
    if (value === null || value === undefined) return false;
    return typeof value === 'object';
  });
};

const getCommandEnvelope = (message: any) => {
  const candidates = [message?.telemetryMessage, message?.message, message];
  const preferred = candidates.find(
    (candidate: any) =>
      candidate &&
      typeof candidate === 'object' &&
      (Object.prototype.hasOwnProperty.call(candidate, 'command_id') ||
        Object.prototype.hasOwnProperty.call(candidate, 'result') ||
        Object.prototype.hasOwnProperty.call(candidate, 'error'))
  );

  return preferred ?? message?.telemetryMessage ?? message?.message ?? message;
};

const getCommandField = (primary: any, fallback: any, field: string) => {
  const lookupPaths = [
    field,
    `payload.${field}`,
    `params.${field}`,
    `data.${field}`,
  ];

  for (const path of lookupPaths) {
    const primaryValue = get(primary, path);
    if (primaryValue !== undefined) return primaryValue;
  }

  for (const path of lookupPaths) {
    const fallbackValue = get(fallback, path);
    if (fallbackValue !== undefined) return fallbackValue;
  }

  return undefined;
};

const edgeTypes = {
  default: StepEdge,
  connection: ConnectionEdge,
  assetConnection: AssetConnectionEdge,
};

const nodeTypes = {
  cloudNode: CloudNode,
  assetNode: AssetNode,
  edgeNode: EdgeNode,
  machineNode: MachineNode,
  smartAssetNode: SmartAssetNode,
};

const markerEnd = {
  type: MarkerType.Arrow,
  width: 40,
  height: 40,
  color: '#a7b1c6',
};

export type NodeItem = {
  id: string;
  type: string;
  display: string | ReactNode;
  sourcePosition: Position;
  targetPosition: Position;
  node?: FunctionComponent<NodeProps>;
  panel?: {
    custom?: boolean;
    component?: FunctionComponent<any>;
    schema: any;
    uiSchema: any;
  };
  addPanel?: {
    schema: any;
    uiSchema: any;
  };
  connectedTo?: string;
  selects?: {};
  data?: Record<string, any>;
  extent?: string;
};

const nodeObject: NodeItem[] = [
  // {
  //   id: 'cloud',
  //   type: 'cloudNode',
  //   display: 'Cloud',
  //   node: CloudNode,
  //   sourcePosition: Position.Right,
  //   targetPosition: Position.Left,
  //   panel: {
  //     schema: CloudSchema,
  //     uiSchema: CloudUISchema,
  //   },
  // },
  // {
  //   id: 'edge',
  //   type: 'edgeNode',
  //   display: 'Edge',
  //   node: EdgeNode,
  //   sourcePosition: Position.Left,
  //   targetPosition: Position.Right,
  //   data: {
  //     label: 'Kharadi Room',
  //   },
  //   panel: {
  //     schema: EdgeSchema,
  //     uiSchema: EdgeUISchema,
  //   },
  // },
  {
    id: 'asset_generic',
    type: 'assetNode',
    display: 'Asset',
    node: AssetNode,
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    panel: {
      schema: AssetSchema,
      uiSchema: AssetUISchema,
    },
    data: {
      label: 'Asset',
    },
    extent: 'parent',
  },
  {
    id: 'machine',
    type: 'machineNode',
    display: 'Machine',
    node: MachineNode,
    sourcePosition: Position.Left,
    targetPosition: Position.Right,
    panel: {
      custom: true,
      component: ParamsPanel,
      schema: MachineSchema,
      uiSchema: MachineUISchema,
    },
  },
];

const getNodeList: any = (nodeType: string) => {
  switch (nodeType) {
    case 'cloudNode':
      return {
        type: 'cloudNode',
        display: 'Cloud',
        node: CloudNode,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        panel: {
          schema: CloudSchema,
          uiSchema: CloudUISchema,
        },
      };
    case 'edgeNode':
      return {
        type: 'edgeNode',
        display: 'Edge',
        node: EdgeNode,
        sourcePosition: Position.Left,
        targetPosition: Position.Right,
        panel: {
          schema: EdgeSchema,
          uiSchema: EdgeUISchema,
        },
      };
    case 'assetNode':
      return {
        type: 'assetNode',
        display: 'Asset',
        node: AssetNode,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        panel: {
          schema: AssetSchema,
          uiSchema: AssetUISchema,
        },
        extent: 'parent',
      };
    case 'machineNode':
      return {
        type: 'machineNode',
        display: 'Machine',
        node: MachineNode,
        sourcePosition: Position.Left,
        targetPosition: Position.Right,
        panel: {
          custom: true,
          component: ParamsPanel,
          schema: MachineSchema,
          uiSchema: MachineUISchema,
        },
      };
    default:
      return 'NONE';
  }
};

const addModalStyles = {
  container: {
    width: '99%',
    // height: '400px',
    boxShadow: '0px 0px 14px 0px #0000000D',
    padding: '24px',
    boxSizing: 'border-box',
    marginBlock: '4px',
    borderRadius: '18px',
  },
  header: {
    height: '64px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  tableWrapper: {
    // height: '220px',
    // border: '1px solid red',
    // overflow: 'auto',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
  },
  checkBox: {
    '& .MuiSvgIcon-root': { color: '#5C85FF !important', fontSize: '1.5rem !important' },
  },
  chip: {
    backgroundColor: '#eef3ff',
  },
};

const getNodeName = (nodeType: string | undefined) => {
  switch (nodeType) {
    case 'edgeNode':
      return 'gateway_name';
    case 'machineNode':
      return 'machine_name';
    case 'assetNode':
    default:
      return 'name';
  }
};

const getNodeData = (node: Node) => {
  switch (node.type) {
    case 'edgeNode':
      return {
        cidr_to_scan:
          get(node, 'data.cidr_to_scan') ||
          get(node, 'data.ip') ||
          get(node, 'cidr_to_scan', '') ||
          get(node, 'ip', ''),
        gateway_id: get(node, 'data.gateway_id') || get(node, 'gateway_id', ''),
      };
    case 'machineNode':
      return {
        machine_id: get(node, 'data.machine_id') || get(node, 'machine_id', ''),
      };
    case 'assetNode':
      return {
        options: get(node, 'data.options', []),
      };
    default:
      return {};
  }
};

const Flow = ({
  selectedRowFlowData,
  config,
  setConfig,
  gateways,
  services,
  setEdgeGateways,
  components,
  machines,
  readonly,
  useCase = false,
  raw,
  hasAccessTo,
  assetComponents,
  activeAssetId,
  activeAssets,
  setActiveAssets,
  myUniqueId,
  failedTagsIds,
  setRemovedNodes,
  setRemovedComponents,
  copyConfigurationFromComponent,
  onGatewayImportClick,
  onCommandResponse,
}: any) => {
  const themeData: any = useTheme();
  const isLiveView = useMemo(() => window.location.href.includes('live'), []);
  // const dagreGraph = new dagre.graphlib.Graph();
  // dagreGraph.setDefaultEdgeLabel(() => ({}));

  // const nodeWidth = 500;
  // const nodeHeight = 30;

  const nodeWidth = 402;
  const nodeHeight = 50;

  const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = 'RL') => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    // const isHorizontal = direction === 'RL';
    dagreGraph.setGraph({
      rankdir: direction,
      nodesep: 50,
      ranksep: 150,
      acyclicer: 'greedy',
      ranker: 'network-simplex',
    });

    nodes.forEach((node: Node) => {
      let nodeTypeWidth = nodeWidth;
      let nodeTypeHeight = nodeHeight;

      switch (node.type) {
        case 'cloudNode':
          nodeTypeWidth = 220;
          nodeTypeHeight = 50;
          break;
        case 'edgeNode':
          nodeTypeWidth = 300;
          nodeTypeHeight = 50;
          break;
        case 'assetNode':
          nodeTypeWidth = 402;
          nodeTypeHeight = 50;
          break;
        case 'machineNode':
        default:
          nodeTypeWidth = 320;
          nodeTypeHeight = 50;
          break;
      }

      dagreGraph.setNode(node.id, {
        width: node.width || nodeTypeWidth,
        height: node.height || nodeTypeHeight,
      });
    });

    edges.forEach((edge: Edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const _nodes = nodes.map((_node: Node) => {
      const node = { ..._node };
      const nodeWithPosition = dagreGraph.node(node.id);

      let actualWidth = nodeWidth;
      let actualHeight = nodeHeight;

      switch (node.type) {
        case 'cloudNode':
          actualWidth = node.width || 200;
          break;
        case 'edgeNode':
          actualWidth = node.width || 300;
          break;
        case 'assetNode':
          actualWidth = node.width || 402;
          break;
        default:
          // actualWidth = node.width || 402;
          actualWidth = node.width || 360;
          break;
      }

      node.position = {
        x: nodeWithPosition.x - actualWidth / 2,
        y: nodeWithPosition.y - actualHeight / 2,
      };

      node.draggable = false;
      node.data = {
        name:
          get(_node, getNodeName(_node.type)) ||
          get(_node, `data.${getNodeName(_node.type)}`) ||
          get(_node, 'data.name', ''),
        label: get(_node, 'label', ''),
        ip: get(_node, 'ip') || get(_node, 'data.ip', ''),
        enabled: get(_node, 'enabled', get(_node, 'data.enabled', true)),
        // enabled: true,
        // ...(_node.type === 'edgeNode' && {
        //   cidr_to_scan: get(_node, 'data.cidr_to_scan') || get(_node, 'cidr_to_scan', ''),
        //   gateway_id: get(_node, 'data.gateway_id') || get(_node, 'gateway_id', ''),
        // }),
        // ...(_node.type === 'machineNode' && {
        //   machine_id: get(_node, 'data.machine_id'),
        // }),
        // ...(_node.type === 'assetNode' && {
        //   options: get(_node, 'data.options', []),
        // }),
        ...getNodeData(_node),
      };

      return node;
    });

    return { nodes: _nodes, edges };
  };

  const [nodeList, setNodeList] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    config?.nodes ?? [
      {
        id: 'cloud',
        type: 'cloudNode',
        draggable: false,
        position: { x: 10, y: 50 },
        data: {},
      },
    ]
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(config?.edges ?? []);

  const [scanFailed, setScanFailed] = useState<boolean>(false);
  const [disableScanStop, setDisableScanStop] = useState({});
  const onCommandResponseRef = useRef(onCommandResponse);

  useEffect(() => {
    onCommandResponseRef.current = onCommandResponse;
  }, [onCommandResponse]);

  const [scanning, setScanning] = useState<number[]>([]);
  const [scanningTags, setScanningTags] = useState<string[]>([]);
  const [scanningGateways, setScanningGateways] = useState<string[]>([]);

  const scanningTagsRef = useRef<string[]>(scanningTags);
  const scanningGatewaysRef = useRef<string[]>(scanningGateways);
  const pendingTagCommandsRef = useRef<Set<string>>(new Set());
  const pendingGatewayCommandsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    scanningTagsRef.current = scanningTags;
    pendingTagCommandsRef.current = new Set(scanningTags.map((id) => normalizeCommandId(id)).filter(Boolean));
  }, [scanningTags]);

  useEffect(() => {
    scanningGatewaysRef.current = scanningGateways;
    pendingGatewayCommandsRef.current = new Set(scanningGateways.map((id) => normalizeCommandId(id)).filter(Boolean));
  }, [scanningGateways]);

  useEffect(() => {
    setScanningTags((scanningIds: string[]) => {
      const next = scanningIds.filter((id) => !failedTagsIds.includes(id));
      if (next.length === scanningIds.length && next.every((id, idx) => id === scanningIds[idx])) {
        return scanningIds;
      }
      return next;
    });
  }, [failedTagsIds])

  // useEffect(() => {
  //   setNodes(
  //     config?.nodes ?? [
  //       {
  //         id: 'cloud',
  //         type: 'cloudNode',
  //         draggable: false,
  //         position: {x: 10, y: 50},
  //         data: {},
  //       },
  //     ]
  //   );
  //   // setEdges(config?.edges ?? []);
  // }, [config]);

  const onConnect = useCallback(
    (params: Connection | Edge) => setEdges((eds) => addEdge({ ...params, animated: true, markerEnd }, eds)),
    []
  );

  const reactFlowInstance = useReactFlow();
  const reactFlowRef = useRef<HTMLDivElement | null>(null);
  const { factoryId } = useParams();
  const anchorParams = useRef({ x: 500, y: 400 });
  const connectingNodeId = useRef<Node | undefined | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    type: '',
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    if (!isDrawerOpen) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id !== _activeNode.id) return node;
          return {
            ...node,
            data: {
              ...node.data,
              options: (node.data.options ?? []).map((opt: any) => {
                if (!opt.data?._copy_completed) return opt;
                const { _copy_completed, ...restData } = opt.data;
                return { ...opt, data: { ...restData, select_factory_to_copy_configuration_from: '' } };
              }),
            },
          };
        })
      );
    }
  }, [isDrawerOpen]);
  
  const [panel, setPanel] = useState<{
    show: boolean;
    panel: string;
    node?: Node;
    data?: Record<string, any>;
  }>({ show: false, panel: '' });
  const [nodeElement, setNodeElement] = useState('');
  const [_activeNode, setActiveNode] = useState<Node | any>({});
  const activeNode = useMemo<Node | any>(
    () => nodes.find((node: Node) => node.id === _activeNode.id),
    [nodes, _activeNode]
  );
  const [machineryTableModal, setMachineryTableModal] = useState<{
    isOpen: boolean;
    node: NodeItem;
    from: string | undefined;
  }>({
    isOpen: false,
    node: {
      id: '',
      type: '',
      display: '',
      sourcePosition: Position.Left,
      targetPosition: Position.Left,
    },
    from: '',
  });
  // const [localMachines, setMachines] = useState(machines);
  const localMachines = useMemo(
    () =>
      machines.filter(
        (machine: any) =>
          !nodes.find(
            (node: Node) => ((node.data && node.data.machine_id) || get(node, 'machine_id')) === machine.machine_id
          )
      ),
    [nodes, machines]
  );
  // console.log({localMachines, nodes, machines}, 'nodes=machines');
  // useEffect(() => {
  //   setTimeout(() => reactFlowInstance.fitView(), 10);
  // }, [reactFlowInstance]);

  const pendingHydrationFitViewRef = useRef(false);

  useLayoutEffect(() => {
    if (!reactFlowInstance || nodes.length === 0 || !pendingHydrationFitViewRef.current) return;
    pendingHydrationFitViewRef.current = false;
    setTimeout(() => {
      reactFlowInstance.fitView();
    }, 0);
  }, [reactFlowInstance, nodes.length]);

  const buildLightGraphSignature = useCallback((nodesInput: any[] = [], edgesInput: any[] = []) => {
    const nodeSignature = (nodesInput || [])
      .map((node: any) => {
        const data = get(node, 'data', {}) || {};
        const options = Array.isArray(data.options) ? data.options : [];
        const optionStateSignature = options
          .map((option: any) => {
            const optionData = option?.data ?? {};
            return [
              String(option?.id ?? ''),
              String(option?.component_id ?? ''),
              String(option?.component_name ?? ''),
              String(option?.enabled ?? ''),
              String(option?.operation ?? ''),
              JSON.stringify(optionData),
            ].join(':');
          })
          .sort()
          .join('|');

        return [
          String(node?.id ?? ''),
          String(node?.type ?? ''),
          String(node?.connectedTo ?? ''),
          String(node?.operation ?? ''),
          String(data?.name ?? ''),
          String(data?.ip ?? ''),
          String(data?.cidr_to_scan ?? ''),
          String(data?.enabled ?? ''),
          String(options.length),
          optionStateSignature,
        ].join('~');
      })
      .sort();

    const edgeSignature = (edgesInput || [])
      .map((edge: any) => `${String(edge?.id ?? '')}:${String(edge?.source ?? '')}->${String(edge?.target ?? '')}:${String(edge?.type ?? '')}`)
      .sort();

    return `${nodeSignature.join('||')}##${edgeSignature.join('||')}`;
  }, []);

  const lastAppliedConfigSignatureRef = useRef('');
  const lastPushedGraphSignatureRef = useRef('');

  useEffect(() => {
    if (!config) return;
    const incomingSignature = buildLightGraphSignature(config?.nodes || [], config?.edges || []);

    if (incomingSignature === lastPushedGraphSignatureRef.current) {
      lastAppliedConfigSignatureRef.current = incomingSignature;
      return;
    }

    if (incomingSignature === lastAppliedConfigSignatureRef.current) {
      return;
    }
    lastAppliedConfigSignatureRef.current = incomingSignature;
    pendingHydrationFitViewRef.current = true;

    if (config?.edges?.length > 0) {
      setNodes((_nds: Node[]) => {
        // Always include the cloud node so getIncomers can resolve gateway→cloud edges
        const cloudNode = {
          id: 'cloud',
          type: 'cloudNode',
          draggable: false,
          position: { x: 10, y: 50 },
          data: {},
        };
        const nodesWithCloud = [
          ...config.nodes.filter((n: Node) => n.id !== 'cloud'),
          cloudNode,
        ];
        const _nodes = nodesWithCloud.map((node: Node) => {
          const _node = { ...node } as unknown as NodeItem;
          const incomers = getIncomers(node, nodesWithCloud, config?.edges);
          // Keep persisted parent mapping stable; edge-derived incomer is only a fallback.
          _node.connectedTo = (node as unknown as NodeItem).connectedTo ?? incomers?.[0]?.id;
          return _node as unknown as Node;
        });

        const { nodes: layoutedNodes } = getLayoutedElements(_nodes, config.edges, 'RL');

        // Preserve node data from config through layout recalculation
        const configNodeDataById = new Map(config.nodes.map((n: any) => [n.id, n.data]));
        const resultNodes = layoutedNodes.map((layoutNode: any) => {
          const originalData = configNodeDataById.get(layoutNode.id);
          if (originalData) {
            return { ...layoutNode, data: { ...layoutNode.data, ...originalData } };
          }
          return layoutNode;
        });

        return [...resultNodes];
      });
      setEdges([]);
    } else if (config?.nodes) {
      const tempEdges = [
        ...(config.nodes
          .filter((node: NodeItem) => !!node.connectedTo)
          .map((node: NodeItem) => ({
            id: node.id + '-generated',
            target: node.id ?? '',
            source: node.connectedTo,
            type: 'step',
            style: {
              strokeDasharray: '5,5',
              strokeWidth: '2',
              stroke: '#51648F80',
            },
          })) as unknown as Edge[]),
      ];

      setNodes((_nds: Node[]) => {
        let nodes = config?.nodes;
        const _nodes = [
          ...nodes.filter((node: Node) => node.id !== 'cloud'),
          {
            id: 'cloud',
            type: 'cloudNode',
            draggable: false,
            position: { x: 10, y: 50 },
            data: {},
          },
        ];
        // const _nodes = nodes.map((node: Node) => {
        //   // const _node = {...node} as unknown as NodeItem;
        //   // const incomers = getIncomers(node, nodes, config?.edges);
        //   // _node.connectedTo = incomers?.[0]?.id;
        //   return _node as unknown as Node;
        // });

        const { nodes: layoutedNodes } = getLayoutedElements(_nodes, tempEdges, 'RL');

        // Preserve node data from config through layout recalculation
        const configNodeDataByIdElse = new Map(config.nodes.map((n: any) => [n.id, n.data]));
        const resultNodesElse = layoutedNodes.map((layoutNode: any) => {
          const originalData = configNodeDataByIdElse.get(layoutNode.id);
          if (originalData) {
            return { ...layoutNode, data: { ...layoutNode.data, ...originalData } };
          }
          return layoutNode;
        });

        return [...resultNodesElse];
      });

      // setNodes(config?.nodes ?? [
      //   {
      //     id: 'cloud',
      //     type: 'cloudNode',
      //     draggable: false,
      //     position: {x: 10, y: 50},
      //     data: {},
      //   },
      // ]);
    }
  }, [buildLightGraphSignature, config]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const mqttClient = useRef<mqtt.MqttClient | null>(null);
  const [telemetry, setTelemetry] = useState<Record<string, any>>({});
  const [connections, setConnections] = useState<Record<string, any>>({});

  const connectedEdges: Edge[] = useMemo(() => {
    const _nodes = nodes as unknown as NodeItem[];
    return [
      ...(_nodes
        .filter((node: NodeItem) => !!node.connectedTo)
        .map((node: NodeItem) => ({
          id: node.id + '-generated',
          target: node.id ?? '',
          source: node.connectedTo,
          type: 'step',
          style: {
            strokeDasharray: '5,5',
            strokeWidth: '2',
            stroke: lighten(themeData.palette.custom.light, 0.4),
          },
        })) as unknown as Edge[]),
    ];
  }, [nodes]);

  const edgesWithConnection = useMemo(() => {
    if (readonly) {
      // return connectedEdges.map((edge: Edge) => {
      //   let status = connections[edge.target]?.status ?? 'waiting...';
      //   return { ...edge, type: 'connection', data: { status } };
      // });
      return connectedEdges.map((edge: Edge, idx: number) => {
        let status = connections[edge.target]?.status ?? 'waiting'; //'connected' ??
        const mqttStatus = connections[edge.target]?.mqttStatus ?? 'waiting'; //'connected' ??
        const plcStatus = connections[edge.target]?.plcStatus ?? 'waiting'; //'connected' ??
        const dbStatus =
          connections[edge.target]?.mqttStatus === 'connected' && connections[edge.target]?.plcStatus === 'connected'
            ? connections[edge.target]?.dbStatus
            : 'disconnected'; //'connected' ??

        return { ...edge, data: { status, mqttStatus, plcStatus, dbStatus } }; //type: 'connection',
      });
    }
    return connectedEdges;
  }, [connections, connectedEdges, readonly]);

  // const connectionCallback = useRef(() => {
  //   setConnections((connections: Record<string, any>) => ({
  //     ...connections,
  //     [node.id]: 'connected'
  //   }));
  // });

  const telemetryRef = useRef<Record<string, any>>({});

  const configRef = useRef({ nodes, edges });

  useEffect(() => {
    configRef.current = { nodes, edges };
  }, [nodes, edges]);

  const [smartAssets, setSmartAssets] = useState<any[]>([]);

  useEffect(() => {
    let id: ReturnType<typeof setInterval> | null = null;
    // if (readonly) {
    //Connect only if readonly
    if (!mqttClient.current) {
      //TODO: REPLACE THIS WITH ORIGINAL
      // mqttClient.current = mqtt.connect(`mqtt://${process.env.REACT_APP_MQTT_HOST ?? window.location.host}/mqtt`);
      /**Code changes for https and username password */
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const brokerUrl = `${protocol}://${process.env.REACT_APP_MQTT_HOST ?? window.location.host}/mqtt`;
      const options = {
        username: process.env.MQTT_USERNAME || "testuser", // MQTT_USERNAME
        password: process.env.MQTT_PASSWORD || "testpass", // MQTT_PASSWORD
      };

      mqttClient.current = mqtt.connect(brokerUrl, options);

      /**Old code before https */
      // mqttClient.current = mqtt.connect(`ws://${process.env.REACT_APP_MQTT_HOST ?? window.location.host}/mqtt`);


      // mqttClient.current = connectMQTT(`mqtt://18.221.154.54/mqtt`);
      // mqttClient.current?.connect();
    }
    // const largestRefreshInterval = Math.max(...nodes?.flatMap((node: Node) => node.data?.options?.flatMap((data: any) => +(data?.data?.refresh_interval ?? 0)) ?? 0), 1000);
    // id = setInterval(() => {
    //   setConnections((connections: Record<string, any>) => {
    //     const now = Date.now();
    //     const _connections = {...connections};
    //     for (let node of nodes) {
    //       const nodeId = node.id;
    //       const connection = connections[nodeId] ?? {};
    //       const sinceLast = now - (connection.timestamp ?? now);
    //       if (sinceLast > largestRefreshInterval + 6000) {
    //         _connections[nodeId] = {status: 'disconnected', timestamp: connection.timestamp ?? now};
    //       } else if (sinceLast > largestRefreshInterval * 2) {
    //         _connections[nodeId] = {status: 'error', timestamp: connection.timestamp ?? now};
    //       } else if (sinceLast > largestRefreshInterval * 1.5) {
    //         _connections[nodeId] = {status: 'warning', timestamp: connection.timestamp ?? now};
    //       } else if (!connection.timestamp) {
    //         _connections[nodeId] = {status: 'warning', timestamp: connection.timestamp ?? now};
    //       }
    //     }
    //     return _connections;
    //   });
    // }, largestRefreshInterval / 2);
    mqttClient.current?.on('connect', (connectedEvent: any) => {
      // connection established
      // const topic = `local/+/core/command_response`;
      const topic = window.location.pathname.includes('live') ? `local/+/+/+` : `local/+/core/+`;
      mqttClient.current?.subscribe(topic);
      // nodes?.filter((node: Node) => node.type === 'edgeNode')?.forEach((node: Node) => {
      //   const gateway_id = node.data.gateway_id;
      //   const topic = `local/+/+/telemetry`
      //   mqttClient.current?.subscribe(topic);
      // });
    });
    mqttClient.current?.on('error', (errEvent: any) => {
      // MQTT connection error
    });
    mqttClient.current?.on('message', (topic: string, payload: Buffer) => {
      try {
        const telemetryMessage = JSON.parse(payload.toString());
        const telemetryPayload = getCommandEnvelope(telemetryMessage);
        // const message_type = telemetryMessage?.payload?.message_type;
        const commandId = normalizeCommandId(getCommandField(telemetryPayload, telemetryMessage, 'command_id'));
        const gatewayCommandId = commandId;
        const [_, gateway_id, component_id, message_type] = topic.split('/');

        if (
          (message_type === 'commands' || message_type === 'command_response') &&
          typeof onCommandResponseRef.current === 'function'
        ) {
          try {
            onCommandResponseRef.current({
              topic,
              message: telemetryPayload,
              rawMessage: telemetryMessage,
            });
          } catch (callbackError) {
            // External callback must never block scan UI handling.
            console.error('[FlowScanTrace] onCommandResponse callback failed', callbackError);
          }
        }

        const gateway = configRef.current.nodes.find((node: Node) => {
          const nodeGatewayId = String(get(node, 'data.gateway_id', '')).trim();
          const topicGatewayId = String(gateway_id ?? '').trim();
          if (!nodeGatewayId || !topicGatewayId) return false;
          return nodeGatewayId === topicGatewayId;
        });
        if (component_id === 'smart') {
          setSmartAssets((assets: any[]) => [
            { asset_id: telemetryMessage.asset_id, asset_name: telemetryMessage.asset_name },
            ...assets.filter((a: any) => a.asset_id !== telemetryMessage.asset_id),
          ]);

          setConnections((connections: Record<string, any>) => ({
            ...connections,
            ['smart-' + telemetryMessage.asset_id]: {
              status: 'connected', // ?? 'warning'
              type: 'smart',
              timestamp: Date.now(),
            },
          }));

          setTelemetry((telemetry: Record<string, any>) => {
            const origTele = telemetry[component_id] ?? {};
            const oldProps = origTele.properties ?? [];
            const newProps = telemetryMessage.payload.properties;
            const mergedProps = [
              ...oldProps.filter((p: any) => !newProps.find((_p: any) => _p.name === p.name)),
              ...newProps,
            ];
            const tele = {
              ...telemetry,
              ['smart-' + telemetryMessage.asset_id]: {
                ...telemetryMessage,
                properties: mergedProps,
                timestamp: Date.now(),
              },
              [telemetryMessage.asset_name]: {
                ...telemetryMessage,
                properties: mergedProps,
                timestamp: Date.now(),
              },
            };
            telemetryRef.current = tele;
            return tele;
          });
        } else if (gateway && component_id === 'core' && message_type !== 'command_response') { // Gateway
          const messageStatus = telemetryMessage.payload?.[0]?.value;
          if (message_type === 'heartbeat') {
            setConnections((connections: Record<string, any>) => ({
              ...connections,
              [gateway.id]: {
                ...get(connections, `${gateway.id}`, {}),
                mqttStatus: messageStatus,
                timestamp: Date.now(),
                type: 'gateway',
              },
            }));
          } else if (message_type === "lastwill") {
            setConnections((connections: Record<string, any>) => ({
              ...connections,
              [gateway.id]: {
                ...get(connections, `${gateway.id}`, {}),
                mqttStatus: messageStatus,
                timestamp: Date.now(),
                type: 'gateway',
              },
            }));
          }
        } else if (gateway && message_type === 'heartbeat') { // Component
          const assetId = telemetryMessage?.header?.asset_id;
          const assets = (configRef.current.nodes as unknown[] as NodeItem[]).filter(
            (node: NodeItem) => node.connectedTo === gateway.id
          ) as unknown[] as Node[];
          const asset = assets.find((asset) => asset.id === assetId);

          const messageAddress = telemetryMessage.payload?.[0]?.address;
          const messageValue = telemetryMessage.payload?.[0]?.value;

          if (['mqtt', 'opc', 'last_will', 'Connection-Error', 'data_error'].includes(messageAddress)) {
            let statuses = {};

            if (messageAddress === 'mqtt') {
              statuses = { mqttStatus: messageValue, dbStatus: messageValue };
            } else if (messageAddress === 'opc') {
              statuses = { plcStatus: messageValue, dbStatus: messageValue };
            } else if (messageAddress === 'last_will') {
              statuses = { mqttStatus: 'disconnected', dbStatus: 'disconnected', plcStatus: 'disconnected' };
            } else if (messageAddress === 'Connection-Error') {
              statuses = { mqttStatus: 'connected', plcStatus: 'disconnected', dbStatus: 'disconnected' };
            } else if (messageAddress === 'data_error') {
              statuses = { dbStatus: 'disconnected' };
            }

            const updateConnection = (type: string, id: string | undefined) => ({
              ...statuses,
              timestamp: Date.now(),
              type,
              connected_assets: assets.map((asset: Node) => asset.id),
            });

            setConnections((connections: Record<string, any>) => ({
              ...connections,
              [gateway.id]: {
                ...get(connections, `${gateway.id ?? 'not_found'}`, {}),
                ...updateConnection('gateway', gateway.id),
              },
              [asset?.id ?? 'not_found']: {
                ...get(connections, `${asset?.id ?? 'not_found'}`, {}),
                ...updateConnection('asset', asset?.id),
              },
              [component_id]: {
                ...get(connections, `${component_id ?? 'not_found'}`, {}),
                ...updateConnection('component', component_id),
              },
            }));
          }
        } else if (gateway && message_type === 'telemetry') {
          const assetId = telemetryMessage?.header?.asset_id;
          const assets = (configRef.current.nodes as unknown[] as NodeItem[]).filter(
            (node: NodeItem) => node.connectedTo === gateway.id
          ) as unknown[] as Node[];
          const asset = assets.find((asset) => asset.id === assetId);

          setTelemetry((telemetry: Record<string, any>) => {
            const origTele = telemetry[component_id] ?? {};
            const oldProps = origTele.properties ?? [];
            // const newProps = telemetryMessage.payload.properties;
            const newProps = (telemetryMessage.payload ?? []).map((message: any) => ({
              name: message.name,
              value: message.value,
            }));

            const mergedProps = [
              ...oldProps.filter((p: any) => !newProps.find((_p: any) => _p.name === p.name)),
              ...newProps,
            ];
            const tele = {
              ...telemetry,
              [gateway.id]: {
                ...telemetryMessage,
                properties: mergedProps,
                connected_assets: assets.map((asset: Node) => asset.id),
                timestamp: Date.now(),
              },
              [asset?.id ?? 'not_found']: {
                ...telemetryMessage,
                properties: mergedProps,
                connected_assets: assets.map((asset: Node) => asset.id),
                timestamp: Date.now(),
              },
              [component_id]: {
                ...telemetryMessage,
                properties: mergedProps,
                connected_assets: assets.map((asset: Node) => asset.id),
                timestamp: Date.now(),
              },
            };
            telemetryRef.current = tele;
            return tele;
          });
          const updateConnection = {
            mqttStatus: 'connected',
            plcStatus: 'connected',
            dbStatus: 'connected',
            timestamp: Date.now(),
            connected_assets: assets.map((asset: Node) => asset.id),
          };
          setConnections((connections: Record<string, any>) => ({
            ...connections,
            [gateway.id]: { ...updateConnection, type: 'gateway' },
            [asset?.id ?? 'not_found']: { ...updateConnection, type: 'asset' },
            [component_id]: { ...updateConnection, type: 'component' },
          }));
          if (!!asset) {
            let servers = asset?.data?.options ?? [];
            let asset_connection = 'connected';
            const commonProps: any[] = [];
            const server = servers.find((component: any) => component.id === component_id);

            if (server) {
              const props = server.data.messages?.flatMap((message: any) => message.props) ?? [];
              commonProps.push(
                ...props.filter((prop: any) =>
                  telemetryRef.current?.[component_id]?.properties?.find((p: any) => p.name === prop.key)
                )
              );
              if (
                props.find((prop: any) =>
                  telemetryRef.current?.[component_id]?.properties?.find((p: any) => p.name === prop.key)
                )
              ) {
                //Some data present
                asset_connection = 'connected';
              }
              // if (!props.every((prop: any) => telemetryRef.current?.[component_id]?.properties?.find((p: any) => p.name === prop.key))) { //Some data absent
              //   asset_connection = 'warning';
              //   break;
              // }
              if (
                props.find((prop: any) =>
                  telemetryRef.current?.[component_id]?.properties
                    ?.find((p: any) => p.name === prop.key)
                    ?.value?.toString()
                    ?.toLowerCase()
                    ?.includes('failed')
                )
              ) {
                //Some data has error
                asset_connection = 'error';
                // break;
              }
            }
            const machines = (configRef.current.nodes as unknown[] as NodeItem[]).filter(
              (node: NodeItem) => node.connectedTo === asset?.id
            ) as unknown[] as Node[]; //getOutgoers(asset, nodes, edges);
            setConnections((connections: Record<string, any>) => ({
              ...connections,
              [asset.id]: {
                status: asset_connection,
                mqttStatus: asset_connection,
                plcStatus: asset_connection,
                dbStatus: asset_connection,
                timestamp: Date.now(),
                type: 'asset',
                connected_machines: machines.map((asset: Node) => asset.id),
              },
            }));
            for (let machine of machines) {
              setConnections((connections: Record<string, any>) => ({
                ...connections,
                // [machine.id]: { status: asset_connection, timestamp: Date.now() },
                [machine.id]: {
                  status: asset_connection,
                  mqttStatus: asset_connection,
                  plcStatus: asset_connection,
                  dbStatus: asset_connection,
                  timestamp: Date.now(),
                }
              }));
              // const parts = machine;
              // let status = 'warning';
              // const props = machine.data.props;
              //
              // if (parts.every((part: any) => {
              //   let error = false;
              //   error = !!props.find((prop: any) => prop.value?.toString()?.toLowerCase()?.includes('failed'));
              //   return !error;
              // }) && asset_connection === 'connected') {
              //   status = 'connected';
              // } else if (parts.find((part: any) => commonProps.find((p: any) => p.usedBy === part.part_id))) {
              //   status = 'warning'
              // } else if (parts.find((part: any) => commonProps.find((p: any) => p.usedBy === part.part_id && telemetryProps.find((_p: any) => _p.name === p.key)?.value?.toString()?.toLowerCase()?.includes('failed')))) {
              //   status = 'error'
              // }
            }
          }
        } else if (
          gateway &&
          message_type === 'command_response' &&
          gatewayCommandId &&
          (scanningGatewaysRef.current.includes(gatewayCommandId) ||
            pendingGatewayCommandsRef.current.has(gatewayCommandId))
        ) {
          const errorMessage = normalizeErrorMessage(getCommandField(telemetryPayload, telemetryMessage, 'error'));
          const assets = parseCommandResult(getCommandField(telemetryPayload, telemetryMessage, 'result'));
          const bulkResultText = getLifecycleTextFromResult(assets);
          const isBulkScanStarted = NON_TERMINAL_SCAN_RESULTS.has(bulkResultText);

          if (isBulkScanStarted) {
            return;
          }

          setIsDrawerOpen(false);
          pendingGatewayCommandsRef.current.delete(gatewayCommandId);
          scanningGatewaysRef.current = scanningGatewaysRef.current.filter(
            (id) => normalizeCommandId(id) !== gatewayCommandId
          );
          setScanningGateways((prev) => prev.filter((id) => normalizeCommandId(id) !== gatewayCommandId));

          if (errorMessage) {
            enqueueSnackbar(`Error scanning assets: ${errorMessage}`, {
              key: Date.now(),
              variant: 'error',
              preventDuplicate: true,
              action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(Date.now())} />,
            });
          } else if (TERMINAL_STOP_SCAN_RESULTS.has(bulkResultText)) {
            enqueueSnackbar(`Stopped scanning assets`, {
              key: Date.now(),
              variant: 'info',
              action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(Date.now())} />,
            });
          } else if (TERMINAL_ERROR_SCAN_RESULTS.has(bulkResultText)) {
            enqueueSnackbar(`Error scanning assets: ${bulkResultText}`, {
              key: Date.now(),
              variant: 'error',
              preventDuplicate: true,
              action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(Date.now())} />,
            });
          } else if (assets && get(assets, 'scan')) {
            const scanEntries = extractBulkScanEntries(assets);
            const count = scanEntries.length;
            const snackBarKey = Date.now();
            enqueueSnackbar(`Assets found: ${count}`, {
              key: snackBarKey,
              variant: 'success',
              action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(snackBarKey)} />,
            });
            anchorParams.current.y = gateway.position.y + 500 - count * 280;
            anchorParams.current.x = gateway.position.x + 700;
            setScanning((scanning: number[]) => [...scanning.filter((id: number) => id !== gateway?.data?.gateway_id)]);
            const _assets: [NodeItem, string, boolean][] = [];
            for (let [ip, asset] of scanEntries) {
              if (asset) {
                const foundPorts = Object.keys(asset.tcp ?? {}).filter((port: string) => {
                  return asset.tcp[port].state === 'open';
                });
                const foundServices = services
                  .filter((svc: any) => foundPorts.map((p) => +p).includes(svc.port))
                  .map((svc: any) => svc.service_id);
                const foundComponents = components
                  .map((component: any) => ({
                    ...component,
                    id: uuid.v4().toString(),
                    label: component.component_name,
                    enabled: true,
                  }))
                  .filter((component: any) => foundServices.includes(component.service_id));
                const _asset = {
                  id: uuid.v4().toString(),
                  type: 'assetNode',
                  display: 'Asset',
                  node: AssetNode,
                  sourcePosition: Position.Right,
                  targetPosition: Position.Left,
                  panel: {
                    schema: AssetSchema,
                    uiSchema: AssetUISchema,
                  },
                  data: {
                    label: 'Asset',
                    enabled: true,
                    ip: ip,
                    options: foundComponents,
                    edge: gateway.id,
                  },
                  connectedTo: gateway.id,
                  extent: 'parent',
                  operation: 'added'
                };
                // addNodeFromList(_asset, gateway.id, true);
                _assets.push([{ ..._asset }, gateway.id, true]);
                anchorParams.current.y = anchorParams.current.y + 250 + (services?.length ?? 0) * 30;
              }
            }
            const newNodes = _assets.map(([node, from, checkExisting]: [NodeItem, string, boolean]) => {
              const newNode: any = {
                ...node,
                id: node.id ?? `${node.type}-${uuid.v4().toString()}`,
                sourcePosition: node.sourcePosition,
                targetPosition: node.targetPosition,
                position: {
                  x: 0,
                  y: 0,
                },
                draggable: false,
                type: node.type,
                data: {
                  label: node.display,
                  type: node.id,
                  ...node.data,
                },
              };

              let found = false;
              let oldNode: Node = (configRef.current.nodes as unknown[] as NodeItem[]).find((node: NodeItem) => {
                return node.data?.ip === newNode.data?.ip && node.connectedTo && newNode.connectedTo;
              }) as unknown as Node;
              if (oldNode) {
                found = true;
                oldNode.data = { ...oldNode.data, ...newNode.data };
              }
              // return node;
              // });
              // if (!found) {
              //   newNodes = [newNode, ...nodes];
              // }

              return found ? oldNode : newNode;
            });

            const finalNodes = [
              ...configRef.current.nodes.filter(
                (node: Node) => !newNodes.find((newNode: Node) => newNode.id === node.id)
              ),
              ...newNodes,
            ];

            const { nodes: layoutedNodes } = getLayoutedElements(
              finalNodes,
              [
                ...((finalNodes as unknown[] as NodeItem[]).map((node: NodeItem, index: number) => ({
                  id: `temp-${index}`,
                  source: node.connectedTo,
                  target: node.id,
                })) as unknown[] as Edge[]),
              ],
              'RL'
            );
            // layout recalculated after node add
            // setNodes([...layoutedNodes]);
            // setEdges([...layoutedEdges]);
            // if(!found) {
            //   flushSync(() => {
            //     const _newNode = layoutedNodes.find((node: Node) => node.id === newNode.id);
            //     if(_newNode) reactFlowInstance.addNodes([_newNode]);
            //   });
            //   // setNodes([...layoutedNodes]);
            // }
            // reactFlowInstance.deleteElements({nodes: nodes});
            // reactFlowInstance.addNodes(layoutedNodes);
            setNodes([...layoutedNodes]);
          }
        } else if (
          message_type === 'command_response' &&
          commandId &&
          (scanningTagsRef.current.includes(commandId) || pendingTagCommandsRef.current.has(commandId))
        ) {
          const snackBarKey = `scan-${commandId}`;
          const errorMessage = normalizeErrorMessage(getCommandField(telemetryPayload, telemetryMessage, 'error'));
          const resultRaw = parseCommandResult(getCommandField(telemetryPayload, telemetryMessage, 'result'));
          const resultText = getLifecycleTextFromResult(resultRaw);
          const isTagScanStarted = NON_TERMINAL_SCAN_RESULTS.has(resultText);

          // Started event is non-terminal. Keep scan tracking active until final response arrives.
          if (isTagScanStarted) {
            return;
          }

          pendingTagCommandsRef.current.delete(commandId);
          scanningTagsRef.current = scanningTagsRef.current.filter(
            (id) => normalizeCommandId(id) !== commandId
          );
          setScanningTags((prev) => prev.filter((id) => normalizeCommandId(id) !== commandId));
          setDisableScanStop((pre) => ({ ...pre, [commandId]: false }));

          if (errorMessage) {
            enqueueSnackbar(`Tag scan failed: ${get(telemetryPayload, 'error', '')}`, {
              key: snackBarKey,
              variant: 'error',
              preventDuplicate: true,
              action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(snackBarKey)} />,
            });
            // setScanFailed(true);
          } else if (TERMINAL_STOP_SCAN_RESULTS.has(resultText) || TERMINAL_ERROR_SCAN_RESULTS.has(resultText)) {
            enqueueSnackbar(`Stopped scanning Tags!`, {
              key: snackBarKey,
              variant: TERMINAL_ERROR_SCAN_RESULTS.has(resultText) ? 'error' : 'info',
              action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(snackBarKey)} />,
            });
          } else if (typeof resultRaw !== 'string') {
            const root = resultRaw;
            // if (!root.children) {
            //   enqueueSnackbar('Found scan results!', {
            //     key: snackBarKey,
            //     variant: 'success',
            //     action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(snackBarKey)} />,
            //   });
            //   dispatch(factoryManagementActions.getScanResults(root));
            //   // return;
            // }
            // const flattenData: any = (tag: any) => {
            //   return [tag, ...tag.children.flatMap(flattenData)];
            // };
            // const allTags = flattenData(root);
            // enqueueSnackbar(
            //   `Tags found: ${allTags.filter((tag: any) => !!tag.type && tag.datatype !== 'Unsupported')?.length ?? 0}`,
            //   {
            //     key: snackBarKey,
            //     variant: 'success',
            //     action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(snackBarKey)} />,
            //   }
            // );
            let asset = configRef.current.nodes.find((node: Node) =>
              Array.isArray(get(node, 'data.options', [])) &&
              get(node, 'data.options', []).some((option: any) => option.uuid === commandId)
            );
            if (!asset) {
              const parsedAssetId = String(commandId || '').split(':')[2];
              asset = configRef.current.nodes.find((node: Node) => node.id === parsedAssetId);
            }

            if (!asset) {
              return;
            }

            const assetOption = get(asset, 'data.options', []).find((option: any) => option.uuid === commandId);
            const scanResultSchema = get(assetOption, 'backend_schema.scanResult', {});
            const allTags = normalizeScanResultRows(root, scanResultSchema);
            enqueueSnackbar(
              `Tags found: ${allTags.length ?? 0}`,
              {
                key: snackBarKey,
                variant: 'success',
                action: () => <CancelOutlinedIcon onClick={() => closeSnackbar(snackBarKey)} />,
              }
            );
            if (asset) {
              updateNodeData({
                options: asset.data.options?.map((_option: any) => {
                  const option = { ..._option };
                  if (option.uuid === commandId) {
                    option.data = {
                      ...option.data,
                      scan_result: allTags
                    };
                    option.operation = option.operation || 'edited';
                  }
                  return option;
                }),
                force_update_id: asset.id,
              });
            }
          }
        }
      } catch (e) {
        console.error(e);
      }
    });
    mqttClient.current?.on('error', () => { });
    // }
    return () => {
      mqttClient.current?.removeAllListeners();
      // mqttClient.current?.reconnect();
      if (id) clearInterval(id);
    };
  }, []);

  // useEffect(() => {
  //   for (let edge of selectedRowFlowData) {
  //     if (!nodes.find(node => node.id === edge.id)) {
  //       addNodeFromList({
  //         id: edge.id,
  //         type: 'edgeNode',
  //         display: edge.display,
  //         data: edge.data,
  //         sourcePosition: Position.Right,
  //         targetPosition: Position.Left
  //       }, 'cloud');
  //     }
  //   }
  // }, [selectedRowFlowData]);

  useEffect(() => {
    const nodesToSave = nodes.map((node: Node) => {
      if (!node.data?.options) return node;
      return {
        ...node,
        data: {
          ...node.data,
          options: node.data.options.map((opt: any) => {
            const { select_factory_to_copy_configuration_from, _copy_completed, ...restData } = opt.data ?? {};
            return { ...opt, data: restData };
          }),
        },
      };
    });
    const nextSignature = buildLightGraphSignature(nodesToSave as any[], edges as any[]);
    if (nextSignature === lastPushedGraphSignatureRef.current) {
      return;
    }

    setConfig((prevConfig: any) => {
      const prevSignature = buildLightGraphSignature(prevConfig?.nodes || [], prevConfig?.edges || []);
      if (prevSignature === nextSignature) {
        lastPushedGraphSignatureRef.current = nextSignature;
        return prevConfig;
      }
      lastPushedGraphSignatureRef.current = nextSignature;
      return { ...prevConfig, nodes: nodesToSave, edges };
    });
  }, [buildLightGraphSignature, edges, nodes, setConfig]);

  const onNodeClick = (e: React.MouseEvent, node: Node) => {
    setActiveNode(node);
  };

  // console.log(nodes, 'NODES');
  // console.log(edges, 'EDGES');

  const onAddComponent = (compontentData: {
    asset_id: string | null;
    service_id: number | null;
    component_id: number | null;
    component_name: string;
    docker_image_url: string;
  }) => {
    const modifiedNodes = nodes.map((nd: any) => {
      if (nd.id === compontentData.asset_id) {
        return {
          ...nd,
          data: {
            ...nd.data,
            options: [...nd.data.options, compontentData],
          },
        };
      }
      return nd;
    });
    setNodes(modifiedNodes);
    handleModalClose();
  };

  const renderModalChildren = (data: Record<string, any>) => {
    const key = data.title;
    const type = data.type;
    switch (type || key) {
      case 'add_service':
        const columns = [
          { field: 'component_name', headerName: 'Display Name', flex: 1 },
          // { field: 'location', headerName: 'Location', flex: 1 },
          {
            field: false,
            headerName: '',
            width: 100,
            sortable: false,
            renderCell: ({ row }: any) => {
              return (
                <Button
                  onClick={() => {
                    const comp = { ...row };
                    // delete comp.backend_schema;
                    // delete comp.ui_schema;
                    updateNodeData({
                      ...activeNode.data,
                      options: [
                        ...(activeNode.data?.options ?? []),
                        {
                          ...comp,
                          id: uuid.v4().toString(),
                          label: row.component_name,
                          enabled: false,
                          data: {},
                          uuid: `asset:${myUniqueId}:${activeNode.id}:${comp.component_id}`,
                          operation: 'added',
                        },
                      ],
                    });
                    handleModalClose();
                    // setModal({title: 'Select edge gateway for connecting service', type: 'connect_service', isOpen: true});
                  }}
                  variant={'contained'}
                  size='small'
                >
                  Add
                </Button>
              );
            },
          },
        ];
        const rows = components.filter(
          (component: any) => !activeNode.data?.options?.find((opt: any) => opt.component_id === component.component_id)
        );
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
            <DataTable
              rows={rows}
              columns={columns}
              hideFooter
              pageSizeOptions={[4]}
              pageSize={rows.length}
              autoHeight
              tableHeight='auto'
              hideBorders
              noRowsOverlay={() => (
                <Stack height='100%' alignItems='center' justifyContent='center'>
                  {components.length ? 'All components are already added to the selected asset' : 'No components added'}
                </Stack>
              )}
              otherProps={{
                getRowId: (row: any) => row.component_id,
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Button variant='outlined' onClick={handleModalClose}>
                Close
              </Button>
            </Box>
          </Box>
        );
      case 'Add Component':
        const componentData = {
          docker_image_url: '',
          service_id: 0,
          component_name: '',
          component_id: 0,
          asset_id: '',
        };
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Box sx={{ display: 'flex' }}>
                <InputLabel sx={{ width: '30%' }}>Select Node</InputLabel>
                <SelectComponent
                  options={nodes.map((node: any) => ({ label: node.display || node.data.label, value: node.id }))}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    componentData.asset_id = e.target.value;
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex' }}>
                <InputLabel sx={{ width: '30%' }}>Select Componenet</InputLabel>
                <SelectComponent
                  options={components.map((component: any) => ({
                    name: component.component_name,
                    value: component.component_id,
                    label: component.component_name,
                  }))}
                  onChange={(
                    e: ChangeEvent<HTMLSelectElement>,
                    {
                      props,
                    }: {
                      props: {
                        value: number;
                        children: string;
                      };
                    }
                  ) => {
                    componentData.component_id = Number(e.target.value);
                    componentData.component_name = props.children;
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Button variant='outlined' onClick={handleModalClose}>
                Cancel
              </Button>
              <Button variant='contained' onClick={() => onAddComponent(componentData)}>
                Save
              </Button>
            </Box>
          </Box>
        );
      case 'Business Rules':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <InputLabel>Enter Business Rule</InputLabel>
            <TextField placeholder='Business Rules' />
            <Button variant='contained'>Add</Button>
          </Box>
        );
      default:
        return <>Hello</>;
    }
  };

  const onBtnClick = useCallback((node: string, element: any) => {
    if (node === 'assetNode' && element.type === 'machineNode') {
      setMachineryTableModal({ isOpen: true, node: element, from: element.connectedTo });
    } else if (element.type === 'business_rules' || element.label === 'Business Rules') {
      setShowRules(true);
      nodeRedIframeRef?.current?.contentWindow?.postMessage(
        {
          command: 'loadflows',
          flows: flows,
        },
        '*'
      );
      setIsDrawerOpen(false);
    } else if (['edgeNode', 'assetNode', 'machineNode'].includes(node) && element.type !== 'add_service') {
      setIsDrawerOpen(true);
    } else {
      setModal({ title: element.label, type: element.type, isOpen: true });
    }
    setNodeElement(element);
  }, []);

  const handleModalClose = () => {
    setModal({
      isOpen: false,
      title: '',
      type: '',
    });
  };

  const onPaneContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    anchorParams.current.x = event.clientX;
    anchorParams.current.y = event.clientY;
    setIsMenuOpen(true);
  }, []);

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const resetMachineModalState = () => {
    setMachineryTableModal({
      isOpen: false,
      node: {
        id: '',
        type: '',
        display: '',
        sourcePosition: Position.Left,
        targetPosition: Position.Left,
      },
      from: '',
    });
  };

  const addNodeFromList = useCallback(
    (node: NodeItem, from?: string, checkExisting?: boolean) => {
      // console.log(node, 'ADDING');
      if (reactFlowRef.current) {
        const { x, y } = reactFlowRef.current?.querySelector('.react-flow__viewport')?.getBoundingClientRect() ?? {
          x: 0,
          y: 0,
        };
        const zoom = reactFlowInstance.getZoom();
        const mergedData: any = {
          label: node.display,
          type: node.id,
          ...node.data,
        };

        if (node.type === 'assetNode' || node.type === 'smartAssetNode') {
          mergedData.enabled = true;
          if (Array.isArray(mergedData.options)) {
            mergedData.options = mergedData.options.map((option: any) => ({
              ...option,
              enabled: true,
            }));
          }
        }

        const newNode: any = {
          ...node,
          id: node.id ?? `${node.type}-${uuid.v4().toString()}`,
          sourcePosition: node.sourcePosition,
          targetPosition: node.targetPosition,
          position: {
            x: (anchorParams.current.x - x) / zoom,
            y: (anchorParams.current.y - y) / zoom,
          },
          draggable: false,
          type: node.type,
          data: mergedData,
          operation: 'added',
        };
        // if (newNode.type === 'assetNode') {
        //   // newNode.data.options = [];
        //   // newNode.data.port = '196.0.0.2';
        //   // newNode.pan
        // }
        // if (newNode.type === 'machineNode') {
        //   newNode.display = 'Machine';
        //   newNode.data = {...newNode.data, ...node.data};
        // }
        // reactFlowInstance.addNodes(newNode);
        // if(checkExisting) {
        // setNodes((nds: Node[]) => {
        //   let found = false;
        //   let newNodes = nds.map((node: Node) => {
        //     if (node.data.ip === newNode.data.ip && checkExisting) {
        //       found = true;
        //       node.data = {...node.data, ...newNode.data}
        //     }
        //     return node;
        //   });
        //   if (!found) newNodes = [newNode, ...nds];
        //   console.log('adding', found, newNodes);
        //   return newNodes;
        // });

        let found = false;
        let newNodes = nodes.map((node: Node) => {
          if (node.data.ip === newNode.data.ip && checkExisting) {
            found = true;
            node.data = { ...node.data, ...newNode.data };
          }
          return node;
        });
        if (!found) {
          newNodes = [newNode, ...nodes];
        }

        const { nodes: layoutedNodes } = getLayoutedElements(
          newNodes,
          [...connectedEdges, { id: 'temp', source: newNode.connectedTo, target: newNode.id }],
          'RL'
        );
        // setNodes([...layoutedNodes]);
        // setEdges([...layoutedEdges]);
        // if(!found) {
        //   flushSync(() => {
        //     const _newNode = layoutedNodes.find((node: Node) => node.id === newNode.id);
        //     if(_newNode) reactFlowInstance.addNodes([_newNode]);
        //   });
        //   // setNodes([...layoutedNodes]);
        // }
        // reactFlowInstance.deleteElements({nodes: nodes});
        // reactFlowInstance.addNodes(layoutedNodes);
        setNodes([...layoutedNodes]);

        // } else {
        //   setNodes((nds) => [newNode, ...nds]);
        // }
        // if (!!from) {
        //   setEdges((eds: Edge[]) => {
        //     let found = false;
        //     let newEdges = eds.map((edge: Edge) => {
        //       if (edge.target === newNode.id && edge.source === from) {
        //         found = true;
        //       }
        //       return edge;
        //     })
        //     if (!found) newEdges = [{
        //       id: newNode.id,
        //       target: newNode.id ?? '',
        //       source: from,
        //       type: 'step'
        //     }, ...eds];
        //     return newEdges;
        //   });
        //   // addEdge({id: newNode.id, source: source ?? '', target: newNode.id}, edges)
        // }
        connectingNodeId.current = null;

        if (node.type === 'machineNode') {
          resetMachineModalState();
        }
      }
    },
    [nodes, connectedEdges, reactFlowInstance]
  );

  const renderMachinesTable = () => {
    const cols: GridColDef[] = [
      { field: 'machine_name', headerName: 'Machine Name', flex: 1, editable: true },
      { field: 'machine_manufacturer', headerName: 'Manufacturer', flex: 1, editable: true },
      {
        field: 'Actions',
        editable: false,
        sortable: false,
        renderCell: (params) => {
          const { node, from } = machineryTableModal;

          return (
            <Button
              variant='contained'
              onClick={() => {
                // console.log(params.row, 'MACHINE-PARAMS***')
                addNodeFromList({
                  ...node,
                  connectedTo: from,
                  id: uuid.v4().toString(),
                  display: params.row.machine_name,
                  data: { ...params.row, name: params.row.machine_name },
                });
                // setMachines((localMachines: any) =>
                //   localMachines.filter((machine: any) => machine.id !== params.row.id)
                // );
              }}
            >
              Select
            </Button>
          );
        },
      },
    ];

    return (
      <Modal open={machineryTableModal.isOpen} title='Machines' handleClose={resetMachineModalState}>
        <Box
          sx={{
            width: '100%',
            ...(localMachines.length === 0
              ? {
                height: '250px',
              }
              : { height: `${(localMachines.length > 5 ? 250 : localMachines.length * 50) + 115}px`, padding: '20px' }),
          }}
        >
          <DataGrid
            rows={localMachines}
            columns={cols}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
            disableColumnMenu
            getRowId={(row: any) => row.machine_id}
            sx={{
              '&.MuiDataGrid-root': {
                border: 'none !important',
              },
              '&.MuiDataGrid-root .MuiDataGrid-withBorderColor': {
                borderColor: 'transparent !important',
              },
            }}
          />
        </Box>
      </Modal>
    );
  };

  const onAddNode = useCallback(
    (node: NodeItem, from?: string) => {
      if (node.type === 'machineNode') {
        setMachineryTableModal({ isOpen: true, node, from: 'machine' });
      } else {
        addNodeFromList({ ...node, id: uuid.v4().toString() });
      }
      handleMenuClose();
    },
    [reactFlowInstance]
  );


  // const handleNodesSubmit = (event: any) => {
  //   event.preventDefault();
  //   const reqParams = {
  //     ...addUseCaseData,
  //     use_case_tags: useCaseTagsData,
  //   };
  //   if (isEdit) {
  //     dispatch(buildUseCaseActions.updateUseCaseRequest(reqParams));
  //   } else {
  //     dispatch(buildUseCaseActions.addUseCaseRequest(reqParams));
  //   }
  //   closeModal();
  // };
  const updateNodeData = useCallback(
    (data: any, openedAsset?: boolean) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (data.force_update_id) {
            if (node.id === data.force_update_id) return { ...node, data: { ...node.data, ...data }, operation: get(node, 'operation', 'edited') };
            else return node;
          } else if (node.id === activeNode?.id || node.id === activeNode?.id?.split('|')[0]) {
            return { ...node, data: { ...node.data, ...data }, ...(!openedAsset ? { operation: get(node, 'operation', 'edited') } : {}) };
          } else {
            return node;
          }
        })
      );
    },
    [activeNode?.id]
  );

  const [openConfirm, setOpenConfirm] = useState(false);

  const removeNode = useCallback(
    (nodeId: string) => {
      setEdgeGateways((gateways: Node[]) => [...gateways.filter((n) => n.id !== nodeId)]);

      const findDescendants = (targetNodeId: string, allNodes: Node[]): string[] => {
        const directChildren = (allNodes as unknown as NodeItem[])
          .filter((node: NodeItem) => node.connectedTo === targetNodeId)
          .map((node: NodeItem) => node.id);

        return directChildren.length === 0
          ? []
          : [...directChildren, ...directChildren.flatMap((childId: string) => findDescendants(childId, allNodes))];
      };

      const allDescendants = findDescendants(nodeId, nodes);
      const removedNodeIdSet = new Set([nodeId, ...allDescendants]);

      const rmNodes = (config.nodes || []).filter((n: Node) => removedNodeIdSet.has(String(n.id)));
      if (rmNodes.length) {
        setRemovedNodes((prev: any[]) => {
          const byId = new Map(prev.map((node: any) => [String(node.id), node] as const));
          rmNodes.forEach((node: any) => byId.set(String(node.id), node));
          return Array.from(byId.values());
        });
      }

      // dagreGraph.removeNode(nodeId);
      // try {
      //   allDescendants.forEach((id: string) => {
      //     dagreGraph.removeNode(id);
      //   });
      // } catch (e) {
      //   console.error('Error while removing connected nodes');
      // }

      const newNodes = nodes.filter((n: Node) => !removedNodeIdSet.has(String(n.id)));
      const { nodes: layoutedNodes } = getLayoutedElements(
        newNodes as unknown[] as Node[],
        [...connectedEdges.filter((edge: Edge) => !removedNodeIdSet.has(String(edge.source)) && !removedNodeIdSet.has(String(edge.target)))],
        'RL'
      );
      setNodes([...layoutedNodes]);
      // setNodes([...layoutedNodes]);
      // setEdges([...layoutedEdges]);
      // if(!found) {
      //   flushSync(() => {
      //     const _newNode = layoutedNodes.find((node: Node) => node.id === newNode.id);
      //     if(_newNode) reactFlowInstance.addNodes([_newNode]);
      //   });
      //   // setNodes([...layoutedNodes]);
      // }
      // reactFlowInstance.deleteElements({nodes: nodes});
      // reactFlowInstance.addNodes(layoutedNodes);
      // setNodes([...layoutedNodes]);

      // setNodes((nodes: Node[]) => [...nodes.filter((n) => n.id !== nodeId)]);
      // setEdges((eds: Edge[]) => [...eds.filter((ed) => ed.target !== nodeId && ed.source !== nodeId)]);
    },
    [config.nodes, connectedEdges, nodes, reactFlowInstance, setRemovedNodes]
  );

  const duplicateNode = useCallback((node: Node, connectedEdges: Edge[]) => {
    const newNode = cloneDeep(node);
    newNode.id = uuid.v4().toString();
    newNode.position = { x: node.position.x + 500, y: node.position.y };
    newNode.data.options = newNode.data.options?.map((component: any) => ({ ...component, id: uuid.v4().toString() }));
    setNodes((nodes: Node[]) => [...nodes, newNode]);
    setEdges((eds: Edge[]) => [
      ...eds,
      ...connectedEdges.map((ed: Edge) => ({
        ...ed,
        id: newNode.id,
        target: newNode.id,
      })),
    ]);
  }, []);

  const scanAssets = useCallback(
    (cidr: string) => {
      if (activeNode) {
        setScanning((scanning: number[]) => [...scanning, activeNode?.data?.gateway_id]);

        const commandId = `gateway:${myUniqueId}:${activeNode?.data?.gateway_id}`
        pendingGatewayCommandsRef.current.add(normalizeCommandId(commandId));

        setDisableScanStop((pre) => ({ ...pre, [commandId]: true }));
        setTimeout(() => {
          setDisableScanStop((pre) => ({ ...pre, [commandId]: false }));
        }, 5000);

        // const uid = uuid.v4().toString();
        const topic = `local/${activeNode?.data?.gateway_id}/core/commands`;
        // const message = {
        //   message_type: 'scan_assets_and_services',
        //   cidr,
        //   use_case_id: factoryId,
        //   service_ports: services.map((service: any) => service.port).join(','),
        //   asset_scan_id: uid,
        // };
        const message = {
          name: 'scan',
          command_id: commandId,
          params: {
            scan_type: 'NMAP',
            cidr,
            service_ports: services.map((service: any) => service.port).join(','),
          },
        };
        scanningGatewaysRef.current = Array.from(
          new Set([...scanningGatewaysRef.current, normalizeCommandId(commandId)])
        );
        setScanningGateways((scanningGateways) => {
          if (scanningGateways.some((id) => normalizeCommandId(id) === normalizeCommandId(commandId))) {
            return scanningGateways;
          }
          return [...scanningGateways, commandId];
        });
        const { x, y } = reactFlowRef.current?.querySelector('.react-flow__viewport')?.getBoundingClientRect() ?? {
          x: 0,
          y: 0,
        };
        mqttClient.current?.publish(topic, JSON.stringify(message));
        anchorParams.current.x = activeNode.position.x + x + 150;
        anchorParams.current.y = activeNode.position.y + y;
      }
    },
    [activeNode, services]
  );
  /**
 * Initiates a scan for tags on a specific asset node using the provided gateway and component information.
 * This method sends a message to the MQTT broker to start the tag scanning process.
 * @remarks
 * - The `secrets_config` parameter has been added to include additional configuration for secure communication.
 * - The `username`, `password`, `private_key`, and `certificate` fields are currently included for testing purposes
 *   and can be removed in the future.
 */

  const dispatch = useDispatch();

  const scanTags = useCallback(
    (gateway_id: string, component: any) => {
      if (activeNode && activeNode?.id) {
        const commandId = component.uuid;
        pendingTagCommandsRef.current.add(normalizeCommandId(commandId));
        scanningTagsRef.current = Array.from(
          new Set([...scanningTagsRef.current, normalizeCommandId(commandId)])
        );
        setScanningTags((scanning: string[]) => {
          if (scanning.some((id) => normalizeCommandId(id) === normalizeCommandId(commandId))) {
            return scanning;
          }
          return [...scanning, commandId];
        });
        setScanFailed(false);
        setDisableScanStop((pre) => ({ ...pre, [commandId]: true }));
        setTimeout(() => {
          setDisableScanStop((pre) => ({ ...pre, [commandId]: false }));
        }, 5000);
        const topic = `local/${gateway_id}/core/commands`;

        const service = services.find((service: any) => service.service_id === component.service_id);

        const secretKeys = Object.keys(component.backend_schema.properties).map((key: any) => ({
          ...component.backend_schema.properties[key],
          secret_key: key
        })).filter((prop: any) => prop.secret);

        const selectedSecrets = secretKeys.reduce((acc: any, key: any) => {
          const value = get(component, ['data', key.secret_key], '');
          const hasValue =
            value !== null &&
            value !== undefined &&
            !(typeof value === 'string' && value.trim() === '') &&
            !(Array.isArray(value) && value.length === 0);
          if (!hasValue) return acc;
          return { ...acc, [key.secret_key]: value };
        }, {});

        const hasSelectedSecrets = Object.keys(selectedSecrets).length > 0;

        // const message = {
        //   message_type: 'scan_tags',
        //   asset_id: activeNode.id,
        //   component: component.id,
        //   ip: activeNode.data?.ip,
        //   port: service.port,
        //   scan_root: component.data?.scan_root,
        //   // username: component.data?.username,
        //   // password: component.data?.password,
        //   private_key: component.data?.privateKey,
        //   certificate: component.data?.certificate,
        //   tls_algo: component.data?.tls_algo,
        //   ...secretsData,
        //   // secrets_config: raw?.secrets_config,
        // };
        const removeEmptyValues = (obj: any) => {
          return Object.fromEntries(
            Object.entries(obj).filter(([key, value]) =>
              value !== null &&
              value !== undefined &&
              value !== '' &&
              !(Array.isArray(value) && value.length === 0)
            )
          );
        };

        const normalizeRootTagArray = (value: any): string[] => {
          if (Array.isArray(value)) {
            return value.map((item) => String(item ?? '').trim()).filter(Boolean);
          }

          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return [];

            if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
              try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                  return parsed.map((item) => String(item ?? '').trim()).filter(Boolean);
                }
              } catch {
                // fall through to delimiter-based parsing
              }
            }

            const parts = trimmed
              .split(/\r?\n|,/)
              .map((item) => item.trim())
              .filter(Boolean);

            return parts.length > 0 ? parts : [trimmed];
          }

          if (value === null || value === undefined) return [];

          return [String(value).trim()].filter(Boolean);
        };

        const componentDataRaw = (component.data || {}) as Record<string, any>;
        const normalizedScanRoot = normalizeRootTagArray(componentDataRaw.scan_root);
        const normalizedRootTags = normalizeRootTagArray(componentDataRaw.root_tags);
        const mergedRootTags = normalizedRootTags.length > 0 ? normalizedRootTags : normalizedScanRoot;
        const { scan_root: _omitScanRoot, ...componentDataWithoutScanRoot } = componentDataRaw;
        const componentData: Record<string, any> = {
          ...componentDataWithoutScanRoot,
          ...(mergedRootTags.length > 0 ? { root_tags: mergedRootTags } : {}),
        };

        let kwargs = {
          server_url: `opc.tcp://${activeNode.data?.ip}:${service.port}`,
          ...(!isEmpty(componentData['root_tags']) && { root_tags: componentData['root_tags'] }),
          ...(componentData['tls_algo'] ? { tls_algo: componentData['tls_algo'] } : {}),
          ...(hasSelectedSecrets
            ? selectedSecrets
            : {
              ...(componentData['username'] ? { username: componentData['username'] } : {}),
              ...(componentData['password'] ? { password: componentData['password'] } : {}),
              ...(componentData['privateKey'] ? { private_key: componentData['privateKey'] } : {}),
              ...(componentData['certificate'] ? { certificate: componentData['certificate'] } : {}),
            }),
        };

        kwargs = removeEmptyValues(kwargs);

        // const message = {
        //   message_type: 'scan_tags',
        //   asset_id: activeNode.id,
        //   component: component.id,
        //   ip: activeNode.data?.ip,
        //   port: service.port,
        //   scan_root: component.data?.scan_root,
        //   username: component.data?.username,
        //   password: component.data?.password,
        //   private_key: component.data?.privateKey,
        //   certificate: component.data?.certificate,
        //   tls_algo: component.data?.tls_algo,
        //   // ...secretsData,
        //   // secrets_config: raw?.secrets_config,
        // };

        const message = {
          name: 'scan',
          command_id: component.uuid,
          params: {
            scan_type: 'OPC',
            args: [],
            kwargs,
          },
        };

        const data = {
          command_id: component.uuid,
          args: [],
          url: `${activeNode.data?.ip}:${service.port}`,
          ...componentData
        }
        const payload = get(component, 'backend_schema.payload', {
          name: 'scan',
          params: {
            args: '{{args}}',
            kwargs: {
              password: '{{password}}',
              tls_algo: '{{tls_algo}}',
              username: '{{username}}',
              root_tags: '{{root_tags}}',
              server_url: 'opc.tcp://{{url}}',
              certificate: '{{certificate}}',
              private_key: '{{privateKey}}',
            },
            scan_type: 'OPC',
          },
          command_id: '{{command_id}}',
        });
        const renderPayload = customRender(payload, data);
        const renderedKwargs = get(renderPayload, 'params.kwargs', {});
        if (renderedKwargs && typeof renderedKwargs === 'object') {
          const existingRootTags = renderedKwargs.root_tags;
          const existingScanRoot = renderedKwargs.scan_root;
          const normalizedRootTags = get(componentData, 'root_tags');
          const hasNormalizedRootTags = Array.isArray(normalizedRootTags)
            ? normalizedRootTags.length > 0
            : normalizedRootTags !== undefined && normalizedRootTags !== null && String(normalizedRootTags).trim() !== '';
          const isRootTagsMissing =
            existingRootTags === undefined ||
            existingRootTags === null ||
            existingRootTags === '' ||
            (Array.isArray(existingRootTags) && existingRootTags.length === 0);

          if (
            isRootTagsMissing &&
            hasNormalizedRootTags
          ) {
            renderedKwargs.root_tags = normalizedRootTags;
          }

          if (
            (renderedKwargs.root_tags === undefined ||
              renderedKwargs.root_tags === null ||
              renderedKwargs.root_tags === '' ||
              (Array.isArray(renderedKwargs.root_tags) && renderedKwargs.root_tags.length === 0)) &&
            existingScanRoot !== undefined &&
            existingScanRoot !== null &&
            existingScanRoot !== ''
          ) {
            renderedKwargs.root_tags = existingScanRoot;
          }
          if (Object.prototype.hasOwnProperty.call(renderedKwargs, 'scan_root')) {
            delete renderedKwargs.scan_root;
          }
        }

        const msgPayload = {
          ...renderPayload,
          ...(hasSelectedSecrets ? selectedSecrets : {}),
        };

        const { x, y } = reactFlowRef.current?.querySelector('.react-flow__viewport')?.getBoundingClientRect() ?? {
          x: 0,
          y: 0,
        };
        // mqttClient.current?.publish(topic, JSON.stringify(message));
        // console.log({topic, message}, 'MESSAGE***')
        dispatch(
          factoryManagementActions.scanTagsRequest({
            factory_id: Number(factoryId),
            gateway_id,
            topic,
            component_id: component.id,
            message: msgPayload,
          })
        );
        anchorParams.current.x = activeNode.position.x + x + 150;
        anchorParams.current.y = activeNode.position.y + y;
      }
    },
    [activeNode, services]
  );

  const stopScan = useCallback(
    (gateway_id: string, commandId: string) => {
      const topic = `local/${gateway_id}/core/commands`;
      // const message = {
      //   message_type: 'stop_scan',
      //   asset_id: activeNode.id,
      // };
      const message = {
        name: "scan_stop",
        command_id: commandId,
      }

      mqttClient.current?.publish(topic, JSON.stringify(message));
    },
    [activeNode]
  );

  const [showRules, setShowRules] = useState(false);
  const nodeRedIframeRef = useRef<HTMLIFrameElement | null>(null);

  const [flows, setFlows] = useState({
    flows: config?.flows ?? [],
    rev: '76b72dfd203b68f1b5633e8b091cea62',
  });

  useEffect(() => {
    setFlows({
      flows: config?.flows ?? [],
      rev: '76b72dfd203b68f1b5633e8b091cea62',
    });
  }, [config]);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    window.onmessage = (e) => {
      if (e && typeof e.data !== 'string' && e.data.command === 'saved') {
        // implement save flow logic here.
        // @ts-ignore
        setConfig((config: any) => ({ ...config, flows: e.data.flow.flows }));
        setFlows(e.data.flow.flows);
        setSaving(false);
        setShowRules(false);
        dispatch(
          buildUseCaseActions.updateUseCaseRequest({
            ...raw,
            status: 'draft',
            id: raw.use_case_id,
            config: { gateways: [...config.nodes?.filter((node: Node) => node.type === 'edgeNode')] },
            ui_config: { ...config, flows: e.data.flow.flows, flows_cred: e.data.flow.credentials },
          })
        );
      }
    };
    // const temp = setInterval(() => {
    //   if (nodeRedIframeRef?.current)
    //     // @ts-ignore
    //     nodeRedIframeRef.current?.contentWindow?.postMessage("save", "*");
    // }, 5000);

    // return () => {
    //   clearInterval(temp);
    // }
  }, []);

  const [componentsLoading, setComponentsLoading] = useState(true);
  const [assetCompsData, setAssetCompsData] = useState<any>({});

  useEffect(() => {
    if (!isEmpty(assetComponents)) {
      setNodes((pre: Node[]) =>
        pre.map((node: Node) => {
          if (node.id === activeAssetId) {
            const existingOptions = node.data?.options || [];
            const isSameComponent = (left: any, right: any) => {
              // Primary identity: component_name (normalized). This is the most reliable
              // semantic identity and handles cases where component_id is synthetic (-1)
              // on one side but a real backend id on the other.
              const leftName = String(left?.component_name ?? left?.label ?? '').trim().toLowerCase();
              const rightName = String(right?.component_name ?? right?.label ?? '').trim().toLowerCase();
              if (leftName && rightName && leftName === rightName) return true;

              // Secondary: both have real (positive) component_ids that agree.
              if (left?.component_id !== undefined && right?.component_id !== undefined) {
                const leftId = Number(left.component_id);
                const rightId = Number(right.component_id);
                if (leftId > 0 && rightId > 0) return leftId === rightId;
              }

              // Fallback: shared id or uuid.
              return (
                (left?.id && right?.id && left.id === right.id) ||
                (left?.uuid && right?.uuid && left.uuid === right.uuid)
              );
            };

            const components = (assetComponents[node.id].components || []).map((component: any) => {
              const { operation: _ignoreIncomingOperation, ...incomingComponent } = component || {};
              const comp = existingOptions.find((opt: any) => isSameComponent(opt, component));
              if (comp) {
                const localOperation = get(comp, 'operation');
                return {
                  ...incomingComponent,
                  ...comp,
                  data: { ...(component.data || {}), ...(comp.data || {}) },
                  ...(localOperation ? { operation: localOperation } : {}),
                };
              }
              return incomingComponent;
            });

            // Keep local options that are not present in incoming store/API payload.
            // This prevents unsaved imported components from disappearing after a refresh cycle.
            const localOnlyComponents = existingOptions.filter(
              (localOption: any) => !components.some((incomingOption: any) => isSameComponent(localOption, incomingOption))
            );
            const mergedComponents = [...components, ...localOnlyComponents];

            // Final deduplication by component_name: guards against any residual
            // duplicates caused by component_id mismatches across render cycles.
            const seenCompNames = new Set<string>();
            const dedupedComponents = mergedComponents.filter((opt: any) => {
              const name = String(opt?.component_name ?? opt?.label ?? '').trim().toLowerCase();
              if (!name || seenCompNames.has(name)) return false;
              seenCompNames.add(name);
              return true;
            });

            if (isEqual(dedupedComponents, get(node, 'data.options', []))) {
              return node;
            }

            return {
              ...node,
              data: {
                ...node.data,
                options: [...dedupedComponents],
              },
            };
          }
          return node;
        })
      );
      setComponentsLoading(false);
    }
  }, [assetComponents]);

  useEffect(() => {
    if (!isEmpty(copyConfigurationFromComponent) && !isEmpty(activeNode)) {
      const { component_id, data } = copyConfigurationFromComponent;
      updateNodeData({
        ...activeNode.data,
        options: activeNode.data?.options.map((option: any) => {
          const copiedOption = { ...option };
          if (copiedOption.component_id === component_id) {
            if (option.id === data.id) copiedOption.id = uuid.v4().toString();
               const { copy_configuration_from, select_factory_to_copy_configuration_from, ...rest } = data;
            copiedOption.data = {
             ...(rest ?? {}),
              select_factory_to_copy_configuration_from: option.data?.select_factory_to_copy_configuration_from ?? factoryId,
              _copy_completed: true,
              // id: copiedOption.id,
            };
            const snackBarKey = Date.now();
            enqueueSnackbar(`Data and configuration copied successfully`, {
              key: snackBarKey,
              variant: 'success',
              action: () => <CloseCircleIcon onClick={() => closeSnackbar(snackBarKey)} />,
            });
          }
          setAssetCompsData((prev: any) => ({
            ...prev,
            component: {
              ...prev.component,
              data: { ...copiedOption.data },
            },
          }));
          return copiedOption;
        }),
      });
      dispatch(factoryManagementActions.clearCopyConfigurationFromComponent());
    }
  }, [copyConfigurationFromComponent]);

  const [addModal, setAddModal] = useState({ show: false, type: '', source: '' });

  const showAddmodal = useCallback((type: string, source: string) => {
    setAddModal({ show: true, type, source });
  }, []);

  const closeAddModal = useCallback(() => {
    setAddModal({ show: false, type: '', source: '' });
  }, []);

  const columns: Record<string, any> = useMemo(
    () => ({
      edge_gateway: [
        { field: 'gateway_name', headerName: 'Display Name', flex: 1 },
        {
          field: 'gateway_status',
          headerName: 'Status',
          width: 140,
          headerAlign: 'center',
          align: 'center',
          renderCell: (params: any) => {
            return (
              <Chip sx={addModalStyles.chip} label={params.row.gateway_status} color='primary' variant='outlined' />
            );
          },
        },
        {
          field: false,
          headerName: 'Add',
          width: 100,
          sortable: false,
          renderCell: ({ row }: any) => {
            return (
              <IconButton
                onClick={() => {
                  addNodeFromList(
                    {
                      id: 'edge-' + row.gateway_id,
                      type: 'edgeNode',
                      display: row.display,
                      data: { ...row },
                      sourcePosition: Position.Right,
                      targetPosition: Position.Left,
                      connectedTo: addModal.source,
                    },
                    'cloud'
                  );
                  closeAddModal();
                }}
              >
                <AddCircleIcon style={{ color: themeData.palette.custom.light }} />
              </IconButton>
            );
          },
        },
      ],
      smart_asset: [
        { field: 'asset_name', headerName: 'Display Name', flex: 1 },
        {
          field: 'asset_status',
          headerName: 'Status',
          width: 120,
          headerAlign: 'center',
          align: 'right',
          renderCell: (params: any) => {
            return <Chip sx={addModalStyles.chip} label={'Requested'} color='primary' variant='outlined' />;
          },
        },
        {
          field: false,
          headerName: 'Add',
          width: 100,
          sortable: false,
          renderCell: ({ row }: any) => {
            return (
              <IconButton
                onClick={() => {
                  addNodeFromList(
                    {
                      id: 'smart-' + row.asset_id,
                      type: 'smartAssetNode',
                      display: row.asset_name,
                      data: { ...row },
                      sourcePosition: Position.Right,
                      targetPosition: Position.Left,
                      connectedTo: addModal.source,
                    },
                    'cloud'
                  );
                  closeAddModal();
                }}
              >
                <AddCircleIcon style={{ color: themeData.palette.custom.light }} />
              </IconButton>
            );
          },
        },
      ],
    }),
    [addModal]
  );

  const addData: Record<string, any> = useMemo(
    () => ({
      edge_gateway: gateways
        .filter(
          (gateway: any) =>
            gateway.data_transfer_status === 'provisioned' &&
            !nodes.find((node: Node) => node.data?.gateway_id === gateway.gateway_id)
        )
        .map((gateway: any) => ({
          ...gateway,
          id: gateway.gateway_id,
        })),
      smart_asset: smartAssets
        .filter((asset: any) => !nodes.find((node: Node) => node.data.asset_id === asset.asset_id))
        .map((asset: any) => ({
          ...asset,
          id: asset.asset_id,
        })),
    }),
    [gateways, nodes, edges, smartAssets]
  );

  const renderModalContent = useMemo(
    () => (
      <Modal
        title={`Select ${lowerCase(addModal.type)} to add to Landscape`}
        open={addModal.show}
        handleClose={closeAddModal}
      >
        <Box sx={{ ...addModalStyles.container, backgroundColor: lighten(themeData.palette.background.default, 0.3) }}>
          <Box sx={addModalStyles.tableWrapper}>
            <Tabs
              value={addModal.type}
              onChange={(e, value) => showAddmodal(value, 'cloud')}
              indicatorColor='primary'
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: 'primary', height: '4px' },
                '& .MuiTab-root': { color: '#434d67', paddingBottom: '23px', paddingTop: '23px' },
                '& .Mui-selected': { color: '#434d67', fontWeight: '600' },
              }}
            >
              <Tab
                label='Edge gateways'
                value={'edge_gateway'}
                sx={{
                  'MuiButtonBase-root-MuiTab-root.Mui-selected': {
                    // color: '#434D67',
                    fontWeight: 600,
                  },
                }}
              />
              <Tab
                label='Smart assets'
                value={'smart_asset'}
                sx={{
                  'MuiButtonBase-root-MuiTab-root.Mui-selected': {
                    // color: '#434D67',
                    fontWeight: 600,
                  },
                }}
              />
            </Tabs>
            <DataTable
              rows={addData[addModal.type]}
              columns={columns[addModal.type]}
              hideFooter
              // pageSizeOptions={[4, 10]}
              // autoHeight
              tableHeight='220px'
              pageSize={addData[addModal.type]?.length ?? 10}
              // autoPageSize
              hideBorders
              otherProps={{
                getRowId: (row: any) => row.id,
              }}
            />
          </Box>
          <Box sx={addModalStyles.footer}>
            <Button variant='outlined' onClick={closeAddModal}>
              CANCEL
            </Button>
            {/*<Button variant="contained" onClick={() => setIsSuccessPopupOpen(true)}>*/}
            {/*  SAVE*/}
            {/*</Button>*/}
          </Box>
        </Box>
      </Modal>
    ),
    [gateways, machines, addModal]
  );

  const [jsonFormError, setJsonFormError] = useState<any>({});
  const [secretError, setSecretError] = useState<any>({});
  const [activeComponentId, setActiveComponentId] = useState<number | null>(null);

  const editorContextValue = useMemo(
    () => ({
      addNode: !readonly ? addNodeFromList : (node: NodeItem, from?: string, checkExisting?: boolean) => { },
      showAddModal: !readonly ? showAddmodal : (type: string, source: string) => { },
      openPanel: () => { },
      editable: !readonly,
      updateData: !readonly && !useCase ? updateNodeData : () => { },
      onBtnClick,
      removeNode:
        !readonly && !useCase
          ? (id: string) => {
            setNodeElement(id);
            setOpenConfirm(true);
          }
          : () => { },
      scanAssets: !readonly && !useCase ? scanAssets : () => { },
      scanTags: !readonly && !useCase ? scanTags : () => { },
      stopScan: !readonly && !useCase ? stopScan : () => { },
      duplicateNode: !readonly && !useCase ? duplicateNode : (node: Node, connectedEdges: Edge[]) => { },
      scanning,
      useCase,
      scanningTags,
      setScanningTags,
      assetCompsData,
      scanFailed,
      disableScanStop,
      hasAccessTo,
      scanningGateways,
      isLiveView,
      activeNode,
      activeComponentId,
      onGatewayImportClick: onGatewayImportClick ?? (() => { }),
    }),
    [
      activeComponentId,
      activeNode,
      addNodeFromList,
      assetCompsData,
      disableScanStop,
      duplicateNode,
      hasAccessTo,
      isLiveView,
      onBtnClick,
      onGatewayImportClick,
      readonly,
      scanAssets,
      scanFailed,
      scanTags,
      scanning,
      scanningGateways,
      scanningTags,
      setScanningTags,
      showAddmodal,
      stopScan,
      updateNodeData,
      useCase,
    ]
  );

  const telemetryContextValue = useMemo(() => ({ connections, telemetry }), [connections, telemetry]);

  return (
    <EditorContext.Provider
      value={editorContextValue}
    >
      {openConfirm && (
        <PopUp
          open={openConfirm}
          icon={<AlertIcon color={palette.warning.main} />}
          message='Are you sure you want to delete node?'
          cancel
          cancelLabel='Cancel'
          onCancel={() => setOpenConfirm(false)}
          confirmation
          confirmLabel='Delete'
          onConfirm={() => {
            removeNode(nodeElement);
            setOpenConfirm(false);
          }}
          onClose={() => setOpenConfirm(false)}
        />
      )}
      <TelemetryContext.Provider value={telemetryContextValue}>
        {
          <>
            <Box sx={{ position: 'relative', minWidth: '1080px' }} hidden={!showRules}>
              <LoadingButton
                variant={'outlined'}
                loading={saving}
                sx={{ position: 'absolute', top: '9px', right: '180px' }}
                onClick={() => {
                  // setSaving(true);
                  setShowRules(false);
                }}
              >
                Back
              </LoadingButton>
              <LoadingButton
                variant={'contained'}
                loading={saving}
                sx={{ position: 'absolute', top: '10px', right: '48px' }}
                onClick={() => {
                  // setSaving(true);
                  nodeRedIframeRef.current?.contentWindow?.postMessage({ command: 'save' }, '*');
                }}
                disabled={!(hasAccessTo.create && hasAccessTo.update)}
              >
                Save rule
              </LoadingButton>
              <iframe
                title='Node-Red'
                id='iframe-node-red'
                ref={nodeRedIframeRef}
                style={{
                  // marginTop: '5px',
                  //height: '750px',
                  minHeight: '600px',
                  height: 'calc(100vh - 260px)',
                  border: 'none',
                  borderRadius: '17px',
                  width: '100%',
                }}
                onLoad={(e) => {
                  nodeRedIframeRef?.current?.contentWindow?.postMessage(
                    {
                      command: 'loadflows',
                      flows: flows,
                    },
                    '*'
                  );
                }}
                src={`/nred/`}
              />
            </Box>
            {renderModalContent}
            <ReactFlow
              ref={reactFlowRef}
              hidden={showRules}
              minZoom={0}
              maxZoom={2}
              nodes={nodes}
              edges={edgesWithConnection}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onPaneContextMenu={onPaneContextMenu}
              onNodeClick={onNodeClick}
            >
              <Background variant={BackgroundVariant.Lines} gap={18} />
              <Menu
                id='basic-menu'
                open={isMenuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                anchorReference='anchorPosition'
                anchorPosition={isMenuOpen ? { top: anchorParams.current.y, left: anchorParams.current.x } : undefined}
              >
                {nodeObject.map((item: any) => (
                  <MenuItem key={item.id} onClick={() => onAddNode(item)}>
                    {item.display}
                  </MenuItem>
                ))}
              </Menu>
              <Controls />
              <MiniMap zoomable pannable />
            </ReactFlow>
          </>
        }
        {isDrawerOpen && (
          <Sidebar
            isDrawerOpen={isDrawerOpen}
            readonly={readonly}
            setIsDrawerOpen={setIsDrawerOpen}
            nodes={nodes}
            setNodes={setNodes}
            activeNode={activeNode}
            nodeElement={nodeElement}
            assetCompsData={assetCompsData}
            setAssetCompsData={setAssetCompsData}
            updateNodeData={updateNodeData}
            setJsonFormError={setJsonFormError}
            setSecretError={setSecretError}
            secretError={secretError}
            jsonFormError={jsonFormError}
            hasAccessTo={hasAccessTo}
            componentsLoading={componentsLoading}
            myUniqueId={myUniqueId}
            setRemovedComponents={setRemovedComponents}
            factoryId={factoryId}
            setActiveComponentId={setActiveComponentId}
          />
        )}
        <Modal open={modal.isOpen} title={modal.title} handleClose={handleModalClose}>
          <Box sx={{ width: '100%', height: '200px', padding: '20px' }}>{renderModalChildren(modal)}</Box>
        </Modal>
        {renderMachinesTable()}
      </TelemetryContext.Provider>
    </EditorContext.Provider>
  );
};

const mapStateToProps = (state: any) => ({
  assetComponents: state.factoryManagement.assetComponentsData,
  activeAssetId: state.factoryManagement.activeAssetId,
  myUniqueId: state.factoryManagement.myUniqueId,
  failedTagsIds: state.factoryManagement.scanTagsFailedIds,
  copyConfigurationFromComponent: state.factoryManagement.copyConfigurationFromComponent,
});

export default connect(mapStateToProps)(Flow);
// export default Flow;
